/* eslint-disable max-lines */
import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  OnDestroy,
  Type,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  ValidationErrors,
} from '@angular/forms';
import {
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { select } from '@angular-redux/store';
import {
  cloneDeepWith,
  compact,
  concat,
  difference,
  eq,
  find,
  findIndex,
  flow,
  forEach,
  get,
  has,
  includes,
  isArray,
  isBoolean,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  isPlainObject,
  isString,
  last,
  map,
  max,
  merge,
  omit,
  pick,
  set,
  size,
  split,
  startCase,
  trim,
} from 'lodash';

import { Cart } from '@geniuspos/xenial-data-contracts/types/dm-entities';

import {
  BackendApiType,
  DocumentMessageTypes,
  EHeaderName,
  ENTITY_TYPES,
  EntityValidateResponse,
  IApiListResponse,
  IBaseEntityResponse,
  IBaseEntityType,
  IBulkUpdateDocumentsResult,
  IDocumentErrorMessage,
  IDocumentInfoMessage,
  IDocumentOperationOptions,
  IEntityConfig,
  IEntityValidateRequest,
  IErrorMessageParams,
  IErrorOverride,
  IFieldPreferences,
  IFilterQuery,
  IHttpOptions,
  IInfoMessageParams,
  IKeyValueAny,
  IModalDialogOptions,
  ObjectId,
  SchemaNode,
  IOperationHistory,
  IAuditTrailFormRule,
  IAuditTrailData,
  ICaseInsensitiveUniqueValidatorCustomOptions,
  IUpdatedFieldsMap,
  IAuditLog,
  IDocumentInfo,
  IAuditTrailTempOptions,
  IAuditLogItem,
  IFormListControlMapperOptions,
  FormListData,
  INumberFormatOptions,
} from '@shared/types';
import {
  checkUrlStartSlash,
  getColumnCaptionPath,
  getDataByReference,
  getStartOfLocalDayISOString,
  patchEffectiveDate,
  pickFieldsFromDocument,
  prepareOdataString,
  queryWithEntityIds,
  removeVirtualPropertiesFromObject,
} from '@shared/utils';
import {
  BULK_REQUEST_COUNT,
  defaultCaptionPathForEntityRenderer,
  ENTITY_ID_PATH,
  MAX_PAGE_SIZE,
  SERVER_REQUEST_DELAY_MS,
  TIME_FORMATS,
} from '@config/index';
import {
  BehaviorSubject,
  Observable,
  observableFrom,
  observableOf,
  observableThrowError,
  observableTimer,
  rx,
  Subscription,
} from '@shared/rx';
import { lazyInject } from '@app/containers/base/decorators';
import { PdfDataStructure } from '@app/shared/common/make-pdf/pdf-data-structure.type';
import { batchRequestMap } from '@app/shared/rx-operators/batch-map';
import { AbstractBaseRenderer } from '@components/xvi-grid/custom-components/cell-renderer-components/abstract-base-renderer/abstract-base-renderer';
import { NO_PERMISSION_STATUS } from '@app/shared/constants';
import { DecimalNumber, createParams } from '@app/shared/common';
import { PdfTableHeader } from '@app/shared/common/make-pdf/pdf-table-header.type';
import { APIActions } from '@app/store/actions/api-actions';
import { UiActions } from '@app/store/actions/ui';
import { IUIListState } from '@app/store/reducers/ui';
import {
  FetchFullDataDecorator,
  FetchCurrentFullDataDecorator,
} from './fetch-full-data.decortor';
import { ExpiringStorageService } from '../expiring-storage/expiring-storage.service';
import { StateService } from '../state-service/state.service';
import { STATE_KEYS } from '../state-service/state-selectors';
import { CompanyUtilService } from '../company/company-util.service';
import { HttpErrorService } from '../http-error';
import { PermissionService } from '../session/permission.service';
import { fromPromise } from 'rxjs/internal-compatibility';
import { NumberFormatService } from '../number-format';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';
import { encodeFilterValue } from '@shared/utils/common-utils';
import { ServiceWithCustomHttp } from './service-with-custom-http.service';
import { INTERNAL_GRID_ROW_ID } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component.constants';

export interface IBaseServiceClass<T extends BaseService = BaseService>
  extends Type<T> {
  config: IEntityConfig;
}

export interface IUseUniqueTrueValueWay<L> {
  previousItems: L[];
  newItems: L[];
  fieldPath: string;
  entityIdPath?: string;
}

/**
 * Defines Base service for basic CRUD requests to server
 * To use this class, you should create child class with specified
 * entityType property and extend this BaseService class
 */
@Injectable()
export abstract class BaseService<
    TEntity extends IBaseEntityType = IBaseEntityType,
  >
  extends ServiceWithCustomHttp
  implements OnDestroy
{
  public static config: IEntityConfig;

  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(APIActions) public apiActions: APIActions;
  @lazyInject(HttpErrorService) public httpErrorService: HttpErrorService;
  @lazyInject(CompanyUtilService) public companyService: CompanyUtilService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(PermissionService) public permissionService: PermissionService;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;

  public subscriptions: Subscription[] = [];

  @select(STATE_KEYS.APPLICATION_STARTED)
  public isApplicationStarted$: Observable<boolean>;
  public config: IEntityConfig;
  public entityType: ENTITY_TYPES;
  // Quick filter works only for the fields included in the headerNames (ag-grid restriction)
  public quickFilterFields: string[] = ['name'];
  public captionProjection: string[] = ['name'];
  public listProjection: string[];
  public headerNames: ColDef[] = [{ headerName: 'Name', field: 'name' }];
  public defaultDocument: object = {};
  public defaultQuery: IFilterQuery = {};
  public documentMessages: (IDocumentInfoMessage | IDocumentErrorMessage)[] =
    [];
  public beforeStartListMethods: Function[] = [];
  // Observer may be subscribed in the beforeStartListMethods hook
  // Need to use beforeDestroyListMethods to unsubscribe when the list component is destroyed
  public beforeDestroyListMethods: Function[] = [];
  public needSelectedPdfColumns: boolean = false;
  public operateList: IOperationHistory[] = [];
  public auditTrailTitle: string = '';
  public auditTrailSubTitle: string = '';
  public auditTrailList: IAuditTrailData[] = [];
  public updatedFieldsMap: IUpdatedFieldsMap = {};
  public excludedOperationMap: IUpdatedFieldsMap = {};

  protected uiListParams: IUIListState = {};

  protected dependentData$: BehaviorSubject<Record<string, any>> =
    new BehaviorSubject<Record<string, any>>({});

  private expiringStorage: ExpiringStorageService =
    new ExpiringStorageService();

  public static useUniqueTrueValueWay<T>(
    options: IUseUniqueTrueValueWay<T>,
  ): T[] {
    const newItemsList: any[] = get(options, 'newItems', []);
    const previousItemsList = get(options, 'previousItems', []);
    const fieldPath: string = get(options, 'fieldPath');
    const entityIdPath: string = get(options, 'entityIdPath', ENTITY_ID_PATH);

    let changedItemId;
    newItemsList.forEach((item) => {
      const prevItem = find(previousItemsList, [
        entityIdPath,
        get(item, entityIdPath),
      ]);

      const valueFromPrev = get(prevItem, fieldPath);
      const valueFromNew = get(item, fieldPath);
      if (valueFromNew && valueFromPrev !== valueFromNew) {
        changedItemId = get(item, entityIdPath);
      }
    });

    if (!changedItemId) {
      return newItemsList;
    }

    return map(newItemsList, (incomingItem) => {
      const item = Object.assign({}, incomingItem);
      if (
        get(item, entityIdPath) !== changedItemId &&
        isBoolean(get(item, fieldPath))
      ) {
        set(item, fieldPath, false);
      }

      return item;
    });
  }

  public static getQuickFilterText(
    params: ICellRendererParams & { caption?: IKeyValueAny },
    path?: string,
  ): string {
    return get(
      params.data?.[AbstractBaseRenderer.captionStorageInDataPath],
      path ||
        getColumnCaptionPath(params?.colDef?.field) ||
        defaultCaptionPathForEntityRenderer,
      null,
    );
  }

  /**
   * Method returns dependent data if such was customized in "getMappingsDependenciesFn"
   * widget field data property
   *
   * @param field - Form rules of the field
   */
  public static getDependenciesFromField(
    field: IFieldPreferences,
  ): IBaseEntityType[] {
    const getMappingsDependenciesFn: Function =
      field?.widgetData?.getMappingsDependenciesFn;
    if (!isFunction(getMappingsDependenciesFn)) return [];

    return getMappingsDependenciesFn() ?? [];
  }

  private static handleRequiredFields<T = IBaseEntityType>(
    document: T,
    requiredFieldsToSend: string[],
    skipPickingRequiredFields: boolean = false,
  ): T {
    if (requiredFieldsToSend && !skipPickingRequiredFields) {
      return <T>pick(document, requiredFieldsToSend);
    }

    return document;
  }

  public constructor(public injector: Injector) {
    super(injector);

    // set default backend api type if not set in child service
    set(
      this.config,
      'backendApiType',
      get(new.target, 'config.backendApiType', BackendApiType.XVI),
    );

    // trigger current company get
    this.companyService?.initCurrentCompany();
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public get canManage(): boolean {
    // must have transfer add permission if shown this button
    const entityNameToCheckPermissions =
      this.config.entityNameToCheckPermissions;
    const entityType = this.config.ENTITY_TYPE;

    return this.permissionService.canManage(
      isEmpty(entityNameToCheckPermissions)
        ? entityType
        : entityNameToCheckPermissions,
    );
  }

  public get canAdd(): boolean {
    // must have transfer add permission if shown this button
    const entityNameToCheckPermissions =
      this.config.entityNameToCheckPermissions;
    const entityType = this.config.ENTITY_TYPE;

    return this.permissionService.canAdd(
      isEmpty(entityNameToCheckPermissions)
        ? entityType
        : entityNameToCheckPermissions,
    );
  }

  public ngOnDestroy(): void {
    forEach(this.subscriptions, (subscription) => subscription.unsubscribe());
    this.subscriptions = [];
  }

  /**
   * get list base url
   *
   * @returns list base url
   */
  public getListBaseUrl(): string {
    const trailRoute = get(this.config, 'customEntityRoute.list', '');

    return trailRoute
      ? `/${this.config.apiEndpoint}/${checkUrlStartSlash(trailRoute)}`
      : `/${this.config.apiEndpoint}`;
  }

  /**
   * Method for getting all master entities using OData filters
   *
   * @param userQuery query object with filter, limit, skip props
   * @param options
   */
  @FetchFullDataDecorator()
  public fetchList(
    userQuery?: IFilterQuery,
    options: IDocumentOperationOptions = {},
  ): Observable<IApiListResponse> {
    const headers = this.composeHeadersForUpdateRequest({
      ...options,
      disallowBusinessDateHeaders: true,
    });
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    const params: HttpParams = this.createParams({
      top: MAX_PAGE_SIZE,
      ...query,
    });

    return this.http
      .get<IApiListResponse>(this.getListBaseUrl(), { params, headers })
      .pipe(
        this.catchHttpError(
          options.errorOverride,
          options.showErrorMessage,
          undefined,
          { ...pick(options, ['notCheckPermissionError', 'throwError']) },
        ),
        rx.map((res) => this.backToFrontMapper(res, options)),
      );
  }

  /**
   * Method for getting all current entities using OData filters
   *
   * @param query   query object with filter, limit, skip props
   * @param siteIds array of site Ids
   * @param siteGroupId site group id
   * @param options
   */
  @FetchCurrentFullDataDecorator()
  public fetchCurrentList(
    query: IFilterQuery = {},
    siteIds: ObjectId[] = [],
    siteGroupId: ObjectId = null,
    options: IDocumentOperationOptions = {},
  ): Observable<any> {
    let params: HttpParams = this.createParams({
      top: MAX_PAGE_SIZE,
      ...query,
    });
    params = params.set(
      'effective_date',
      get(query, 'effectiveDate', getStartOfLocalDayISOString()),
    );
    params = params.set(
      'include_mappings',
      get(query, 'include_mappings', 'false') as string,
    );
    params = params.set(
      'split_site_documents',
      get(query, 'split_site_documents', 'true') as string,
    );

    const headers = this.composeHeadersForUpdateRequest(
      options,
      siteIds,
      siteGroupId,
    );

    return this.http
      .get<IApiListResponse>(`/${this.config.apiEndpoint}/current`, {
        params,
        headers,
      })
      .pipe(
        this.catchHttpError(options.errorOverride),
        rx.map((response) => {
          if (Array.isArray(response.items)) {
            return response;
          }
          throw new Error(response.errorMessage);
        }),
        rx.map((res) => this.backToFrontMapper(res, options)),
      );
  }

  public batchFetchDataHandler<T>(
    fetchApi: (...params: T[]) => Observable<any>,
    buildParams: (page: number) => T[],
    buildPageRange: (total: number) => number[],
  ): Observable<IBaseEntityResponse<any>> {
    let firstResponse: IBaseEntityResponse<any> = {
      current_page: 0,
      items: [],
      page_count: 0,
      page_size: 0,
      total: 0,
    };

    return fetchApi.apply(this, buildParams(0)).pipe(
      rx.switchMap((entityResponse: IBaseEntityResponse<any>) => {
        firstResponse = entityResponse;

        return isEqual(firstResponse.total, 0)
          ? []
          : buildPageRange(entityResponse.total);
      }),
      rx.mergeMap((page: number) => fetchApi.apply(this, buildParams(page))),
      rx.reduce(
        (
          preResponse: IBaseEntityResponse<any>,
          curResponse: IBaseEntityResponse<any>,
        ) => {
          return {
            ...curResponse,
            current_page: max([
              preResponse.current_page,
              curResponse.current_page,
            ]),
            items: concat(preResponse.items, curResponse.items),
          };
        },
        firstResponse,
      ),
      rx.map((entityResponse: IBaseEntityResponse<any>) => {
        if (isEmpty(entityResponse.items)) {
          return firstResponse;
        }

        entityResponse.items = concat(
          firstResponse.items,
          entityResponse.items,
        );

        return entityResponse;
      }),
    );
  }

  /**
   * Method for getting document by ID
   *
   * @param id document ID
   * @param options
   */
  public getDocumentById(
    id: string,
    options: IDocumentOperationOptions = {},
  ): Observable<object> {
    const params: HttpParams = this.createParams(options.query);
    const headers = this.composeHeadersForUpdateRequest({
      ...options,
      disallowBusinessDateHeaders: true,
    });

    return this.http
      .get(`/${this.config.apiEndpoint}/${id}`, { params, headers })
      .pipe(
        this.catchHttpError(options.errorOverride),
        rx.map((item) => {
          const newItem = this.formatFieldsWithTimeZone(item);

          return options.skipMappers
            ? newItem
            : this.backToFrontItemMapper(newItem);
        }),
      );
  }

  /**
   * Method for getting document by ID
   *
   * @param ids - document IDs
   * @returns
   */
  public getDocumentByIds(
    ids: ObjectId[],
    options: IDocumentOperationOptions = {},
  ): Observable<any> {
    const initialQuery: IFilterQuery = { filter: `_id eq in(${ids})` };
    const query = { ...initialQuery, ...options.query };

    return this.fetchList(query, options);
  }

  public fetchGetDocumentById(
    documentId: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    options: IDocumentOperationOptions = {},
  ): any {}

  public getDocumentByIdWithSkipUpdateDocument(
    documentId: string,
    options: IDocumentOperationOptions = {},
  ): Promise<IBaseEntityType> {
    return this.fetchGetDocumentById(documentId, {
      ...options,
      skipUpdateDocument: true,
    });
  }

  /**
   * Method for creating new document
   *
   * @param document entity document object
   * @param options
   */
  public createDocument(document, options: IHttpOptions = {}): Observable<any> {
    const body = this.frontToBackItemMapper(document);

    return this.http.post(`/${this.config.apiEndpoint}`, body, options).pipe(
      this.catchHttpError(),
      rx.map((item) => this.backToFrontItemMapper(item)),
    );
  }

  /**
   * Method for creating new document
   *
   * @param{Object} document entity document object
   * @param{string} id document ID
   * @param{IDocumentOperationOptions&IHttpOptions} options request options
   */
  public updateDocument(
    document,
    id: string,
    options: IDocumentOperationOptions & IHttpOptions = {},
  ): Observable<any> {
    const body = this.prepareDataForRequest(document, options);

    return this.http
      .patch(`/${this.config.apiEndpoint}/document/${id}`, body, options)
      .pipe(
        this.catchHttpError(options.errorOverride),
        rx.map((item) => this.backToFrontItemMapper(item)),
      );
  }

  /**
   * Method for change document status
   */
  public changeStatusById(
    documentId,
    entityIdPath,
    targetStatus,
  ): Observable<any> {
    const body = {
      status: targetStatus,
    };

    return this.http
      .patch(`/${this.config.apiEndpoint}/${documentId}`, body)
      .pipe(this.catchHttpError());
  }

  /**
   * Method for deleting by _id
   *
   * @param document entity document object
   */
  public deleteDocument(document): Observable<object> {
    return this.http
      .delete(`/${this.config.apiEndpoint}/document/${document._id}`)
      .pipe(
        rx.catchError(({ error }: HttpErrorResponse) =>
          observableThrowError(get(error, 'errors')),
        ),
      );
  }

  /**
   * Method for getting cart data by document id
   *
   * @param entityId  document entity ID
   * @param options
   */
  public fetchCartByDocumentId(
    documentId: string,
    options: IDocumentOperationOptions = {},
  ): Observable<Cart> {
    const params: HttpParams = this.createParams(options.query);

    return this.http.get<Cart>(`/cart/document/${documentId}`, { params }).pipe(
      this.catchHttpError(options.errorOverride, true, false, {
        throwError: true,
      }),
    );
  }

  /**
   * Method for deleting master document by entity_id
   *
   * @param entityId of product
   */
  public deleteDocumentByEntityId(entityId: ObjectId): Observable<object> {
    return this.http
      .delete(`/${this.config.apiEndpoint}/entity/${entityId}`)
      .pipe(this.catchHttpError());
  }

  /**
   * Method for getting document schema for current document
   */
  public fetchSchema(): Observable<object> {
    const cacheSchema = (schema): any =>
      this.expiringStorage.setItem(
        `schema_${this.config.apiEndpoint}`,
        schema,
        60 * 60, // 1 hour
      );

    const schemaFromCache$ = observableFrom(
      this.expiringStorage.getItem(`schema_${this.config.apiEndpoint}`),
    );

    const loadSchema$ = this.http
      .get(`/${this.config.apiEndpoint}/schema`)
      .pipe(
        this.catchHttpError(),
        rx.map(this.replaceSchemaReferences.bind(this)),
        rx.map(this.extendSchemaWithUISpecificFieldsMapper.bind(this)),
        rx.tap(cacheSchema),
      );

    return this.isApplicationStarted$.pipe(
      rx.filter((isApplicationStarted) => isApplicationStarted),
      rx.switchMap(() => {
        return schemaFromCache$.pipe(
          rx.switchMap((schema) =>
            schema ? observableOf(schema) : loadSchema$,
          ),
        );
      }),
    );
  }

  /**
   * Method for create document
   *
   * @param documents document object with optional fields - _id, mappings
   * @param options
   */
  public createEntity(
    documents,
    options: IDocumentOperationOptions = {},
  ): Observable<IBaseEntityType> {
    const headers = this.composeHeadersForUpdateRequest(options);
    const preparedDocuments = this.prepareDataForRequest(documents, options);
    const endpoint = options?.endpoint ?? '';
    const url = `/${this.config.apiEndpoint}/${endpoint}`;

    return this.http
      .post<any>(url, preparedDocuments, { headers })
      .pipe(this.catchHttpError(options.errorOverride));
  }

  /**
   * Method for update document
   *
   * @param document document object with optional fields - _id, mappings
   * @param options
   */
  public updateEntity(
    document,
    options: IDocumentOperationOptions = {},
  ): Observable<IBaseEntityType> {
    const headers = this.composeHeadersForUpdateRequest(options);
    const preparedDocuments = this.prepareDataForRequest(document, options);
    const endpoint = options?.endpoint ? `${options.endpoint}/` : '';
    const url = `/${this.config.apiEndpoint}/${endpoint}`;

    return this.http.patch<any>(url, preparedDocuments, { headers }).pipe(
      this.catchHttpError(
        options.errorOverride,
        get(options, 'showErrorMessage', false),
        false,
        {
          throwError: true,
        },
      ),
    );
  }

  /**
   * Method for check is operation valid
   *
   * @param validateRequest check valid options
   * @param options request options
   */
  public validateChange(
    validateRequest: IEntityValidateRequest,
    options: IDocumentOperationOptions = {},
  ): Observable<EntityValidateResponse> {
    const headers = this.composeHeadersForUpdateRequest(options);
    const endpoint = options?.endpoint ? `${options.endpoint}/` : '';
    const url = `/${this.config.apiEndpoint}/${endpoint}validate-change`;

    // we think validate should be silently, so it's will catch and not pop out error message
    return this.http
      .post<EntityValidateResponse>(url, validateRequest, { headers })
      .pipe(
        rx.catchError(({ error }: HttpErrorResponse) => {
          if (isEqual(error.status, NO_PERMISSION_STATUS)) {
            return observableOf([]);
          }

          return observableThrowError(error);
        }),
      );
  }

  /**
   * Method for bulk-update document
   *
   * @param document document object with optional fields - _id, mappings
   * @param options
   */
  public bulkUpdate(
    document,
    options: IDocumentOperationOptions = {},
  ): Observable<IBaseEntityType[]> {
    const headers = this.composeHeadersForUpdateRequest(options);
    const endpoint = options?.endpoint ?? '';
    const backendApiType = this.config.backendApiType;
    const url = `/${this.config.apiEndpoint}/${endpoint}`;
    const httpOptions = {
      headers,
      params: {
        backendApiType,
      },
    };

    return this.http
      .post<any>(url, { items: document }, httpOptions)
      .pipe(this.catchHttpError(options.errorOverride));
  }

  public bulkUpdateEntity(
    documents: IBaseEntityType[],
    options: IDocumentOperationOptions = {},
    batchCount: number = BULK_REQUEST_COUNT,
    isCanUpdateObs: (doc: IBaseEntityType) => Observable<boolean> = null,
  ): Observable<IBulkUpdateDocumentsResult> {
    const updateResult: IBulkUpdateDocumentsResult = {
      totalCount: size(documents),
      updated_documents: [],
      successCount: 0,
    };

    if (isEqual(updateResult.totalCount, 0)) {
      return observableOf(updateResult);
    }

    return batchRequestMap(
      documents,
      (document) =>
        (isFunction(isCanUpdateObs)
          ? isCanUpdateObs(document)
          : observableOf(true)
        ).pipe(
          rx.switchMap((isCanUpdate) =>
            !isCanUpdate
              ? observableOf(false) // think is update failed if can't update
              : this.updateEntity(omit(document, '_id'), {
                  ...options,
                  endpoint: document._id,
                }).pipe(
                  rx.map(() => {
                    updateResult.updated_documents.push(document);

                    return true;
                  }),
                  rx.catchError(() => {
                    return observableOf(false);
                  }),
                ),
          ),
        ),
      batchCount,
    ).pipe(
      rx.reduce(
        (successCount, isCurSuccess) =>
          isCurSuccess ? successCount + 1 : successCount,
        updateResult.successCount,
      ),
      rx.map((successCount) => {
        updateResult.successCount = successCount;

        return updateResult;
      }),
    );
  }

  public bulkCreateEntity(
    documents: IBaseEntityType[],
    options: IDocumentOperationOptions = {},
    batchCount: number = BULK_REQUEST_COUNT,
  ): Observable<IBulkUpdateDocumentsResult> {
    const createResult: IBulkUpdateDocumentsResult = {
      totalCount: size(documents),
      successCount: 0,
      created_documents: [],
    };

    if (isEqual(createResult.totalCount, 0)) {
      return observableOf(createResult);
    }

    return batchRequestMap(
      documents,
      (document) =>
        this.createEntity(document, { endpoint: document._id }).pipe(
          rx.switchMap((savedDocument) => {
            if (options.needSubmit && !isEmpty(savedDocument._id)) {
              return this.updateEntity(options?.requestPayload || {}, {
                ...options,
                endpoint: savedDocument._id,
              }).pipe(
                rx.catchError(() => observableOf(true)),
                rx.map(() => observableOf(true)),
              );
            }
            if (!isEmpty(savedDocument._id)) {
              createResult.created_documents.push({ ...savedDocument });
            }

            return observableOf(!isEmpty(savedDocument._id));
          }),
          rx.catchError(() => {
            return observableOf(false);
          }),
        ),
      batchCount,
    ).pipe(
      rx.reduce(
        (successCount, isCurSuccess) =>
          isCurSuccess ? successCount + 1 : successCount,
        createResult.successCount,
      ),
      rx.map((successCount) => {
        createResult.successCount = successCount;

        return createResult;
      }),
    );
  }

  /**
   * Method which creates HttpParams and returns
   */
  public createParams(query: IFilterQuery = {}): HttpParams {
    const backendApiType = this.config.backendApiType;
    // If api type is 'xvi' or 'portal', encode 'contains' values
    const needEncodeDomains = [BackendApiType.XVI, BackendApiType.PORTAL];
    if (includes(needEncodeDomains, backendApiType)) {
      const filterString = get(query, 'filter', '');
      if (!isEmpty(filterString)) {
        query.filter = filterString.replace(
          /contains\(([^,]+),'([^']+)'\)/g,
          (_match, field, value) =>
            `contains(${field},'${encodeFilterValue(value)}')`,
        );
      }
    }

    return createParams({ config: this.config, ...query });
  }

  /** Following mapper functions are called for each http request.
   * These functions map particular document of response (using documnet mapper)
   * from the backend version to the frontend one and vice versa.
   * By default these function return same document, but they can be overriden in children to
   * keep all document mappings at the only location.
   *
   * To keep it consistent, please add following mappers to any new HTTP request.
   * Do not forget to add backToFront mapper to update request, because its result is a document.
   */

  /**
   * frontToBackMapper maps request with multiple items using item mapper
   * this function currently is not used due to absence of update endpoints.
   */
  public frontToBackMapper(req): any {
    if (req.items) {
      return Object.assign(req, {
        items: req.items.map(this.frontToBackItemMapper),
      });
    }

    return req;
  }

  public get needConvertTimeZoneFields(): string[] {
    return get(this, 'config.needConvertTimeZoneFields', []);
  }

  public get timeZoneTimeFormats(): TIME_FORMATS {
    return get(this, 'config.timeZoneTimeFormats', TIME_FORMATS.L);
  }

  public formatFieldsWithTimeZone(item: IBaseEntityType): IBaseEntityType {
    if (isEmpty(this.needConvertTimeZoneFields)) {
      return item;
    }

    return this.companyService.covertDateBetweenUTCAndCurrentTimeZone(
      item,
      this.needConvertTimeZoneFields,
      true,
      this.timeZoneTimeFormats,
    );
  }

  /**
   * response mapper
   */
  public backToFrontMapper(
    apiResponse: IApiListResponse,
    options: IDocumentOperationOptions = {},
  ): any {
    const isNeedConvertFieldsToTimeZone = !isEmpty(
      this.needConvertTimeZoneFields,
    );
    if (!isNeedConvertFieldsToTimeZone && options.skipMappers) {
      return apiResponse;
    }

    if (!has(apiResponse, 'items') && isPlainObject(apiResponse)) {
      return this.backToFrontPlainResMapper(
        apiResponse,
        isNeedConvertFieldsToTimeZone,
        options,
      );
    }

    return Object.assign(apiResponse, {
      items: apiResponse.items.map((item) =>
        this.mapBackToFrontItem(item, isNeedConvertFieldsToTimeZone, options),
      ),
    });
  }

  public backToFrontPlainResMapper(
    apiResponse: IBaseEntityType | IBaseEntityType[],
    isNeedConvertFieldsToTimeZone: boolean = true,
    options: IDocumentOperationOptions = {},
  ): IApiListResponse<IBaseEntityType> {
    if (has(apiResponse, 'items') || !isPlainObject(apiResponse)) {
      return apiResponse as any;
    }

    const arrayLikeResponse = isArray(apiResponse)
      ? apiResponse
      : [apiResponse];

    return {
      items: map(arrayLikeResponse, (res) =>
        this.mapBackToFrontItem(res, isNeedConvertFieldsToTimeZone, options),
      ),
      page_size: size(arrayLikeResponse),
      total: size(arrayLikeResponse),
      current_page: 1,
      page_count: 1,
    };
  }

  public mapBackToFrontItem(
    item: IBaseEntityType,
    isNeedConvertFieldsToTimeZone: boolean = true,
    options: IDocumentOperationOptions = {},
  ): IBaseEntityType {
    let newItem = item;
    if (isNeedConvertFieldsToTimeZone) {
      newItem = this.formatFieldsWithTimeZone(item);
    }

    return options.skipMappers ? newItem : this.backToFrontItemMapper(newItem);
  }

  protected getPayloadBlackList(): string[] {
    return [];
  }

  /**
   * item mappers
   */
  public frontToBackItemMapper(doc: IBaseEntityType): any {
    const requiredFieldsToSend = get(this, 'config.requiredFieldsToSend', []);
    const convertDateFields = get(this, 'config.needConvertTimeZoneFields', []);

    return flow(
      (document): IBaseEntityType =>
        removeVirtualPropertiesFromObject(document),
      (document): IBaseEntityType => patchEffectiveDate(document),
      (document): IBaseEntityType =>
        pickFieldsFromDocument(document, requiredFieldsToSend),
      (document): IBaseEntityType =>
        this.companyService.covertDateBetweenUTCAndCurrentTimeZone(
          document,
          convertDateFields,
        ),
      (document): IBaseEntityType => {
        const blackList = this.getPayloadBlackList();
        if (isEmpty(blackList)) return document;
        let result = document;
        forEach(blackList, (itemKey) => {
          if (!includes(itemKey, '[each]')) {
            result = omit(result, itemKey);
          } else {
            const currentItemSplitKeys = split(itemKey, '.');
            const arrayIndex =
              findIndex(currentItemSplitKeys, (item) => eq(item, '[each]')) - 1;

            forEach(
              result[currentItemSplitKeys[arrayIndex]],
              (arrayItem, index) => {
                result = omit(
                  result,
                  `${currentItemSplitKeys[arrayIndex]}[${index}].${currentItemSplitKeys[arrayIndex + 2]}`,
                );
              },
            );
          }
        });

        return result;
      },
    )(doc);
  }

  public backToFrontItemMapper(doc: IBaseEntityType): IBaseEntityType {
    return doc;
  }

  protected getEntityName(item: TEntity): string {
    // we have to use 'entity_name' for backward compatibility
    return get(
      item,
      'name',
      get(item, 'document_change.name', get(item, 'entity_name', 'N/A')),
    );
  }

  public getItemValue(item: TEntity): string {
    return get(item, ENTITY_ID_PATH, '');
  }

  public getItemText(item: TEntity): string {
    return get(item, 'name', '');
  }

  public getSchemaId(item: TEntity): string {
    return get(item, 'id', '');
  }

  public checkUniqueValidator(
    additionalFilter?: string,
    fieldName?: string,
  ): AsyncValidatorFn {
    return (control): any =>
      observableTimer(SERVER_REQUEST_DELAY_MS).pipe(
        rx.switchMap((): any => {
          const value: string = Array.isArray(control.value)
            ? `in(${control.value.map((val) => prepareOdataString(val))})`
            : `'${prepareOdataString(control.value)}'`;

          const fieldPath: string = Object.keys(control.parent.controls).find(
            (key) => control.parent.controls[key] === control,
          );
          let filter: string = `${fieldPath} eq ${value}`;
          filter = this.adjustFilterWithEntityId(
            filter,
            control.parent.value.entity_id,
          );
          if (additionalFilter && isFunction(additionalFilter)) {
            filter += ` and ${additionalFilter()}`;
          }
          if (additionalFilter && !isFunction(additionalFilter)) {
            filter += ` and ${additionalFilter}`;
          }

          return this.fetchList({ filter, top: 1 }).pipe(
            rx.map(({ items }) => {
              if (!items.length) return null;

              return this.composeValidationError(control, fieldName, fieldPath);
            }),
          );
        }),
      );
  }

  public caseInsensitiveUniqueValidator(
    fieldName?: string,
    customValidOptions: ICaseInsensitiveUniqueValidatorCustomOptions = {},
  ): AsyncValidatorFn {
    return (control): any =>
      observableTimer(SERVER_REQUEST_DELAY_MS).pipe(
        rx.switchMap(() => {
          const {
            currentEntityId,
            customErrorMessage,
            include_active = false,
          } = customValidOptions;
          const value: string = prepareOdataString(trim(control.value), true);
          if (isEmpty(value)) {
            return observableOf(null);
          }
          const fieldPath: string = Object.keys(control.parent.controls).find(
            (key) => control.parent.controls[key] === control,
          );

          let filter: string = include_active
            ? `${fieldPath} eq caseinsensitive(${value}) and is_active eq true`
            : `${fieldPath} eq caseinsensitive(${value})`;

          const entityId = get(
            control,
            'parent.controls.entity_id.value',
            currentEntityId,
          );
          filter = this.adjustFilterWithEntityId(filter, entityId);

          return this.fetchList({ filter, top: 1 }).pipe(
            rx.map(({ items }) => {
              if (isEmpty(items)) return null;

              return this.composeValidationError(
                control,
                fieldName,
                fieldPath,
                customErrorMessage,
              );
            }),
          );
        }),
      );
  }

  public getCheckUniqueNumberValidator(fieldName?: string): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> =>
      observableTimer(SERVER_REQUEST_DELAY_MS).pipe(
        rx.switchMap(() => {
          const value: DecimalNumber = new DecimalNumber(control.value);
          const fieldPath: string = Object.keys(control.parent.controls).find(
            (key) => control.parent.controls[key] === control,
          );
          let filter: string = `${fieldPath} eq ${value?.toString()}`;

          const entityId = get(control, 'parent.controls.entity_id.value');
          filter = this.adjustFilterWithEntityId(filter, entityId);

          return this.fetchList({ filter, top: 1 }).pipe(
            rx.map(({ items }) => {
              if (isEmpty(items)) return null;

              return this.composeValidationError(control, fieldName, fieldPath);
            }),
          );
        }),
      );
  }

  public composeValidationError(
    control: AbstractControl,
    fieldName: string,
    fieldPath: string,
    customErrorMessage?: string,
  ): ValidationErrors {
    /**
     * If "fieldPath" contains nested path like 'property_0.property_1'
     * we should take the last property name
     */
    const name: string = fieldName || startCase(last(fieldPath.split('.')));

    /**
     * Due to some angular problem, form status doesn't get updated after form generation.
     * The next line allows to show the errors of the async validator.
     */
    setTimeout(() => control.root.updateValueAndValidity(), 0);
    const errorMsgTemplate = customErrorMessage
      ? customErrorMessage
      : I18n.ErrorMessages.ValueAlreadyInUse;

    const errorMsg = this.i18nService.translate(errorMsgTemplate, {
      value: control.value,
      fieldName: name,
    });

    return { error: errorMsg };
  }

  public composeRemovingValidationErrorMessage(
    count: number,
    isSingleSelection: boolean = true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    options: Record<string, string> = {},
  ): IModalDialogOptions | string {
    if (isSingleSelection) {
      // eslint-disable-next-line max-len
      return `This ${startCase(
        this.config.ENTITY_TYPE,
      )} is currently associated to an entity and cannot be removed, please remove this ${startCase(
        this.config.ENTITY_TYPE,
      )} from all entities and try again.`;
    }

    // eslint-disable-next-line max-len
    return `Some ${startCase(
      this.config.ENTITY_TYPE,
    )}s are currently associated to an entity and cannot be removed, please remove these ${startCase(
      this.config.ENTITY_TYPE,
    )}s from all entities and try again.`;
  }

  public replaceSchemaReferences(schema): object {
    return cloneDeepWith(schema, (fieldValue) => {
      const reference = get(fieldValue, '$ref', null);

      if (reference) {
        const referenceData = getDataByReference(schema, reference);

        return referenceData
          ? merge({}, referenceData, omit(fieldValue, '$ref'))
          : fieldValue;
      }
    });
  }

  public async getInfoMessageInformation(): Promise<
    (IDocumentInfoMessage | IDocumentErrorMessage)[]
  > {
    const message: IDocumentErrorMessage | IDocumentInfoMessage = await (<
      IDocumentInfoMessage | IDocumentErrorMessage
    >flow(this.deletedDocumentHandler.bind(this))());
    if (message) this.documentMessages.push(message);

    return this.documentMessages;
  }

  public clearDocumentMessages(): void {
    this.documentMessages = [];
  }

  /**
   * Method to modify Schema with Entity Specific Fields - should be reassigned in specific service
   */
  public extendSchemaWithUISpecificFieldsMapper(
    schema: SchemaNode,
  ): SchemaNode {
    return schema;
  }

  protected createInfoMessage(
    params: IInfoMessageParams,
  ): IDocumentInfoMessage {
    return {
      type: DocumentMessageTypes.INFO,
      message: params.message,
    };
  }

  protected createErrorMessage(
    params: IErrorMessageParams,
  ): IDocumentErrorMessage {
    return {
      type: DocumentMessageTypes.ERROR,
      message: params.message,
      errors: params.errors,
    };
  }

  protected async getMasterDocsByEntityIds(
    entityIds: ObjectId[],
  ): Promise<IBaseEntityType[]> {
    if (!Array.isArray(entityIds) || !entityIds.length) {
      return;
    }
    const query: IFilterQuery = queryWithEntityIds(entityIds);
    query.select = ['_id', ENTITY_ID_PATH];
    query.filter += ' and is_master eq in(true)';

    const response = await this.fetchList(query).toPromise();

    return get(response, 'items', []);
  }

  /* eslint-disable */
  protected catchHttpError =
    (
      errorOverride?: IErrorOverride,
      showErrorMessage: boolean = false,
      isBrokenWhenNoPermission: boolean = false,
      options?: { throwError?: boolean; notCheckPermissionError?: boolean },
    ) =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError(({ error }: HttpErrorResponse) => {
          const { notCheckPermissionError = false, throwError = false } =
            options ?? {};

          if (
            isEqual(error.status, NO_PERMISSION_STATUS) &&
            !notCheckPermissionError
          ) {
            return this.httpErrorService
              .catchNoPermissionHttpError([error], { isBrokenWhenNoPermission })
              .pipe(
                rx.switchMap((result: any) => {
                  if (throwError) {
                    return observableThrowError(error);
                  }

                  return observableOf(result);
                }),
              );
          }

          if (showErrorMessage) {
            const toast = {
              errors: {
                title: error.title,
                message: error?.meta?.message || '',
              },
            };

            this.uiActions.showErrorToasterMessage(toast);
          }

          return observableThrowError(error);
        }),
      );

  public trimFieldValue(
    fieldPath: string | string[],
    doc,
  ): (doc: IBaseEntityType) => IBaseEntityType {
    if (!has(doc, fieldPath)) return doc;

    const fieldValue: string = get(doc, fieldPath, '');

    if (!isString(fieldValue)) return fieldValue;

    return set(doc, fieldPath, fieldValue.trim());
  }

  public composeHeadersForUpdateRequest(
    options: IDocumentOperationOptions = {},
    siteIds: ObjectId[] = [],
    siteGroupId?: ObjectId,
  ): HttpHeaders {
    const headersOptions = get(options, 'headers', {});
    if (!isEmpty(siteIds))
      headersOptions[EHeaderName.XSiteIds] = siteIds.join();
    if (!isNil(siteGroupId))
      headersOptions[EHeaderName.XSiteGroupId] = siteGroupId;

    return new HttpHeaders(headersOptions);
  }

  protected getDocumentIds(): ObjectId[] {
    return this.stateService.getByPath(STATE_KEYS.UI_EDITOR_DOCUMENT_IDS) || [];
  }

  public transformDocumentToPdfStructure(
    document: IBaseEntityType,
    headers?: PdfTableHeader[],
  ): PdfDataStructure {
    return;
  }

  public getPdfHeaders(document: IBaseEntityType): PdfTableHeader[] {
    return;
  }

  public acknowledgeDocument(id: string): Promise<any> {
    return this.http
      .patch(`/${this.config.apiEndpoint}/${id}/acknowledge`, null)
      .pipe(this.catchHttpError())
      .toPromise();
  }

  public batchAcknowledgeDocuments<T = any>(
    documentIds: ObjectId[],
  ): Observable<T> {
    return batchRequestMap(
      documentIds,
      (documentId) => fromPromise(this.acknowledgeDocument(documentId)),
      10,
    );
  }

  public prepareDataForRequest<T = IBaseEntityType>(
    data: T[] | T,
    options: IDocumentOperationOptions,
  ): T[] | T {
    const skipPickingRequiredFields = get(
      options,
      'skipPickingRequiredFields',
      false,
    );
    const requiredFieldsToSend = get(options, 'requiredToSendFields', null);
    const mappedDoc = this.documentForRequestHandler(data);

    return BaseService.handleRequiredFields(
      mappedDoc,
      requiredFieldsToSend,
      skipPickingRequiredFields,
    );
  }

  public adjustFilterWithEntityId(filter, entityId): string {
    return entityId ? `${filter} and entity_id eq nin(${entityId})` : filter;
  }

  public getAuditTrailTemp(
    data: IDocumentInfo[] = [],
    options: IAuditTrailTempOptions,
  ): IAuditTrailFormRule {
    return null;
  }

  public applyAuditLogsWithPopulatedLogs(
    populatedLogs: IAuditLog[],
    endpointName = '',
  ): any[] {
    return [];
  }

  /**
   * Method for delete
   *
   * @param{string}  _id
   * @param{IDocumentOperationOptions&IHttpOptions} options request options
   */
  public deleteItems(
    id: string,
    options: IDocumentOperationOptions & IHttpOptions = {},
  ): Observable<any> {
    return this.http
      .delete(`/${this.config.apiEndpoint}/${id}`)
      .pipe(this.catchHttpError(options.errorOverride));
  }

  public formateNumberForStartEnd(
    startEnd: IAuditLogItem,
    customFormatOpts?: INumberFormatOptions,
  ): string {
    if (isNil(get(startEnd, 'start')) && isNil(get(startEnd, 'end'))) {
      return '';
    }

    const customFormatter = isNil(customFormatOpts)
      ? this.numberFormatService.getNumberFormatter(false)
      : this.numberFormatService.getNumberFormatter(
          false,
          undefined,
          customFormatOpts,
        );

    const formateNumber = (path: string) => {
      let startEndNumber = get(startEnd, path, null);
      if (!isNil(startEndNumber)) {
        startEndNumber = customFormatter({
          value: startEndNumber,
        });
      } else {
        startEndNumber = '';
      }

      return startEndNumber;
    };

    return `${formateNumber('start')} → ${formateNumber('end')}`;
  }

  public async duplicate(entities?: IBaseEntityType[]): Promise<void> {}

  /**
   * Mapper for preparing data from the form-list grid to the form-list control
   * @param { BaseEntityType[] } gridData - data from the form-list grid
   * @param { FormControl } control - the main form-list control
   * @param { ObjectId } siteIds - site ids for which will be generated prices
   * @param { IFormListControlMapperOptions[] } options - mapper options
   * @returns { unknown }
   */
  public gridToFormListControlMapper(
    gridData: IBaseEntityType[],
    control: FormControl,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    options: IFormListControlMapperOptions = {},
  ): FormListData {
    const { partialUpdate } = options;

    if (partialUpdate) {
      const partialUpdateIdentifyPath = get(
        partialUpdate,
        'identifyPath',
        INTERNAL_GRID_ROW_ID,
      );

      // If control value is empty, return grid data as is
      if (isEmpty(control.value)) {
        return gridData;
      }

      const controlIds = map(control.value, partialUpdateIdentifyPath);
      const gridIds = map(gridData, partialUpdateIdentifyPath);

      const addOnIds = difference(gridIds, controlIds);

      return concat(
        map(control.value, (controlItem) => {
          const gridItem = gridData.find(
            (item) =>
              item[partialUpdateIdentifyPath] ===
              controlItem[partialUpdateIdentifyPath],
          );

          return isEmpty(gridItem) ? controlItem : gridItem;
        }),
        // new items that were added in the grid and not exist in the control value yet
        compact(
          map(addOnIds, (addOnId) => {
            return gridData.find((item) =>
              isEqual(item[partialUpdateIdentifyPath], addOnId),
            );
          }),
        ),
      );
    }

    return gridData;
  }

  /**
   * Mapper for preparing data from the form-list grid to the form-list control
   * @param { FormControl } control - the main form-list control
   * @param { BaseEntityType[] } gridItems - items from the form-list grid
   * @param { ObjectId } siteIds - list of current site ids
   * @returns { BaseEntityType[] }
   */
  public formListControlToGridMapper(
    control: FormControl,
    data: {
      gridItems: IBaseEntityType[];
      dependentData?: Record<string, IApiListResponse<IBaseEntityType>>;
    },
  ): IBaseEntityType[] {
    const { gridItems, dependentData } = data;

    this.dependentData$.next(dependentData || {});

    return gridItems;
  }

  private deletedDocumentHandler(): IDocumentInfoMessage {
    return this.createErrorMessage({
      errors: [],
      message: `One or more packages has failed to deploy`,
    });
  }

  private documentForRequestHandler<T = IBaseEntityType>(document: T): T {
    return this.frontToBackItemMapper(document);
  }
  // eslint-disable-next-line max-lines
}
